// ============================================================
// TMR Portal Admin — validação dos dados bancários, espelhando o domínio
// ============================================================
import type { BankAccountDetailsInput } from './contract';

/** Field-level messages, keyed by form field. Empty means the form may be submitted. */
export type BankAccountErrors = Partial<Record<keyof BankAccountDetailsInput, string>>;

/**
 * Validates a receiving account's bank details.
 *
 * It mirrors `BankAccountDetails.Create` in the Finance domain, field by field, and exists because the
 * alternative is what the screen did before: check that nothing is blank, submit, and let the operator
 * discover the real rules one server round-trip at a time — as a single message with no field attached,
 * because the backend returns the first failure and nothing says which box it came from.
 *
 * The server is still the authority (it validates the same things and the UI never replaces it). What this
 * buys is that a branch with one digit is refused next to the branch field, before anybody waits.
 *
 * The digit-count rules are checked on the digits alone, exactly as the domain does: it strips
 * non-digits before measuring, so "1.234-5" and "12345" are the same branch.
 */
export function validateBankAccount(form: BankAccountDetailsInput): BankAccountErrors {
  const errors: BankAccountErrors = {};

  if (!form.holderLegalName.trim()) errors.holderLegalName = 'Informe o titular da conta';
  if (!form.bankName.trim()) errors.bankName = 'Informe o banco';

  const code = digits(form.bankCode);
  if (code.length < 1 || code.length > 3) errors.bankCode = 'O código do banco tem de 1 a 3 dígitos';

  const branch = digits(form.branch);
  if (branch.length < 3 || branch.length > 5) errors.branch = 'A agência tem de 3 a 5 dígitos';

  const number = digits(form.number);
  if (number.length < 4 || number.length > 12) errors.number = 'A conta tem de 4 a 12 dígitos';

  const digit = form.digit.trim().toUpperCase();
  if (digit.length < 1 || digit.length > 2 || !/^[0-9X]+$/.test(digit)) {
    errors.digit = 'O dígito tem 1 ou 2 caracteres (números ou X)';
  }

  return errors;
}

/** The digits of a value, ignoring masks — the same normalization the domain applies before measuring. */
function digits(value: string): string {
  return value.replace(/\D/g, '');
}
